import type { CustomStoreImplementationOptions, KaElementMap, MergeCustomStore, StoreIdentifier } from 'kapix-components-vue3'
import { defineStore } from 'pinia'
import { initAliveStoreIds, kapixContext, openUrl, remove, scrollInfo } from 'kapix-components-vue3'
import formationsMarketingDigitalStoreCustom from './store.custom'
import { $translate } from '~/modules/i18n'

const storeName = 'formationsMarketingDigital'
const customImplement: CustomStoreImplementationOptions = formationsMarketingDigitalStoreCustom.options
const aliveStoreIds = initAliveStoreIds()

function formationsMarketingDigitalFactory (storeId?: Nullable<StoreIdentifier>) {
  return defineStore(storeId == null ? storeName : `${storeName}/${storeId}`, {
    state: () => {
      return {
        $aliveStoreIds: aliveStoreIds,
        $subStoreIds: 1,
        $storeId: storeId,
        $emit: (() => {}) as (propName: any, value: any) => void,
        $elements: {} as KaElementMap,
        $router: kapixContext.isClient ? kapixContext.$router : undefined,
        $route: kapixContext.isClient ? kapixContext.$route : undefined,
        // Page info,
        $title: $translate('prog-03-marketing-digital.title', 'Formation en marketing digital'),
        $description: $translate('prog-03-marketing-digital.description', 'Boostez votre présence en ligne grâce aux techniques du marketing digital. Coaché par l\'un de nos experts, vous pourrez mettre en place une stratégie efficace pour VOTRE entreprise.'),
        $publishedTime: new Date('2023-02-13T07:42:17.610Z'),
        $modifiedTime: new Date('2023-09-06T12:37:17.445Z'),
        // Constants,
        $constants: {
          blocPreviewProfilExpertTags: [
            { Nom: 'Inbound marketing' },
            { Nom: 'Hubspot' },
            { Nom: 'E-mail marketing' },
            { Nom: 'CRM' },
            { Nom: 'SEO' },
            { Nom: 'Stratégie commerciale' },
            { Nom: 'Contenu' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element1Tags: [
            { Nom: 'Acquisition' },
            { Nom: 'E-commerce' },
            { Nom: 'SEO' },
            { Nom: 'E-mailing' },
            { Nom: 'Communication 360°' },
            { Nom: 'Fidélisation' },
            { Nom: 'Social media/Ads' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element2Tags: [
            { Nom: 'Tunnel de conversion' },
            { Nom: 'SEO' },
            { Nom: 'CRM' },
            { Nom: 'Inbound marketing' },
            { Nom: 'Hubspot' },
            { Nom: 'Stratégie digitale' },
            { Nom: 'Cybersécurité' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          element3Tags: [
            { Nom: 'Tunnel de conversion' },
            { Nom: 'Automation' },
            { Nom: 'Inbound marketing' },
            { Nom: 'Hubspot' },
            { Nom: 'Stratégie digitale' },
            { Nom: 'CRM' },
            { Nom: 'Funnel' }
          ] as PartialEntities<Kapix.Entity.ITagsExperts>,
          dropDownFormationArguments: [
            { Text: '<ul><li>Maîtriser les grandes tendances comportementales à l’ère du digital.</li></ul>' },
            { Text: '<ul><li>Publier avec différents formats de communication : site web, blog, newsletter, réseaux sociaux.</li></ul>' },
            { Text: '<ul><li>Utiliser les canaux d’acquisition digitale.</li></ul>' },
            { Text: '<ul><li>&nbsp;Approfondir les différents leviers du marketing digital et leurs interactions avec le modèle POEM (Paid, Owned &amp; Earned Media).</li></ul>' },
            { Text: '<ul><li>Optimiser la création de contenu au service du marketing digital.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element4Arguments: [
            { Text: '<ul><li>Appréhender le marché de la recherche en ligne.</li></ul>' },
            { Text: '<ul><li>&nbsp;Optimiser sa fiche Google My Business.</li></ul>' },
            { Text: '<ul><li>Maîtriser les 3 piliers du référencement naturel : technique, éditorial et popularité.&nbsp;</li></ul>' },
            { Text: '<ul><li>Maîtriser le référencement payant : l’audience, l’annonce, la notation.</li></ul>' },
            { Text: '<ul><li>Établir un panorama de ses canaux d\'acquisition (Google, Facebook, Instagram, Linkedin...).</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element5Arguments: [
            { Text: '<ul><li>Cartographier les parcours de ses clients.</li></ul>' },
            { Text: '<ul><li>Analyser le découpage du parcours client : la découverte du service, considération du service et décision d’adhérer à un service.</li></ul>' },
            { Text: '<ul><li>&nbsp;Maîtriser les outils et les usages du marketing automation.</li></ul>' },
            { Text: '<ul><li>Définir dans sa stratégie, ce qui peut être automatisé et ce qui ne le peut pas.</li></ul>' },
            { Text: '<ul><li>L’emailing, outil incontournable du marketing digital.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element6Arguments: [
            { Text: '<ul><li>Identifier ses personas.</li></ul>' },
            { Text: '<ul><li>Construire son funnel marketing.</li></ul>' },
            { Text: '<ul><li>&nbsp;Choisir les call to action à adresser aux clients.</li></ul>' },
            { Text: '<ul><li>Développer un content mapping : en fonction des personas.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>,
          element7Arguments: [
            { Text: '<ul><li>Définir la psychologie et biais cognitifs des utilisateurs.</li></ul>' },
            { Text: '<ul><li>Connaître les outils pour suivre le comportement d’un utilisateur.</li></ul>' },
            { Text: '<ul><li>Analyser le comportement de ses personas, et leurs parcours sur son site.</li></ul>' },
            { Text: '<ul><li>Créer des landing page pour acquérir de nouveaux clients.</li></ul>' },
            { Text: '<ul><li>Maîtriser les techniques avancées du copywriting.</li></ul>' }
          ] as PartialEntities<Kapix.Entity.IDropDownFormation>
        },
        // Data,
        $info: { prog03MarketingDigital: scrollInfo() },
        ...(customImplement.state && customImplement.state())
      }
    },
    getters: {
      ...customImplement.getters
    },
    actions: {
      /* @ts-ignore: to allow override in your custom file */
      blocPreviewProfilExpertClick () {
        openUrl('https://www.matchers.fr/expert/288', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      element1Click () {
        openUrl('https://www.matchers.fr/expert/306', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      element2Click () {
        openUrl('https://www.matchers.fr/expert/97', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      element3Click () {
        openUrl('https://www.matchers.fr/expert/158', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      voirClick () {
        openUrl('https://www.matchers.fr/experts', '_self')
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        return storeId != null ? getStoreInstance(storeId) : this
      },
      /* @ts-ignore: to allow override in your custom file */
      getStoreInstances () {
        return aliveStoreIds.map(storeId => this.getStoreInstance(storeId))
      },
      /* @ts-ignore: to allow override in your custom file */
      newStoreInstance (storeId?: Nullable<StoreIdentifier>) {
        const newStoreId = storeId || this.$subStoreIds++
        if (aliveStoreIds.includes(newStoreId)) {
          throw new Error(`Store with id ${storeId} already exists`)
        }
        aliveStoreIds.push(newStoreId)
        return getStoreInstance(newStoreId)
      },
      ...customImplement.actions,
      /* @ts-ignore: to allow override in your custom file */
      beforeMount () {
        customImplement.actions?.beforeMount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      mounted () {
        customImplement.actions?.mounted?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      async serverPrefetch () {
        await customImplement.actions?.serverPrefetch?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      beforeUnmount () {
        customImplement.actions?.beforeUnmount?.call(this)
      },
      /* @ts-ignore: to allow override in your custom file */
      unmounted () {
        if (this.$storeId != null) {
          this.$dispose()
          remove(aliveStoreIds, this.$storeId)
        }
        customImplement.actions?.unmounted?.call(this)
      }
    }
  })
}

function getStoreInstance (storeId?: Nullable<StoreIdentifier>) {
  return formationsMarketingDigitalFactory(storeId)()
}

export const formationsMarketingDigitalRaw = formationsMarketingDigitalFactory()
export const formationsMarketingDigital = () => formationsMarketingDigitalRaw() as MergeCustomStore<typeof formationsMarketingDigitalStoreCustom.instance>
